<template>
  <modal :show.sync="internalShow" @close="handleClose" size="lg" :modalClasses="'modal-xl'">
    <template #header>
      <div class="text-xl font-semibold">Invoice Payment</div>
    </template>
    <div class="invoice-payment-popup">
      <!-- Customer Summary -->
      <div
        class="p-4 bg-gray-100 border-l-4 border-blue-500 rounded-lg customer-summary"
      >
        <h4 class="mb-2 font-bold">Customer Information</h4>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div class="flex flex-col">
            <strong>Customer:</strong>
            <span class="text-blue-600">{{ customerName }}</span>
          </div>
          <div class="flex flex-col">
            <strong>Firm:</strong>
            <span class="text-blue-600">{{ firmName }}</span>
          </div>
          <div class="flex flex-col">
            <strong>Total Invoices:</strong>
            <span class="text-blue-600">{{ selectedInvoices.length }}</span>
          </div>
        </div>
      </div>

      <!-- Invoice List -->
      <div class="mt-4 invoice-list">
        <h4 class="mb-2 font-bold">Invoice Details</h4>
        <div class="overflow-x-auto">
          <table class="min-w-full bg-white border border-gray-200 rounded-lg">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase border-b">
                  Invoice No.
                </th>
                <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase border-b">
                  Date
                </th>
                <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase border-b">
                  Invoice Amount
                </th>
                <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase border-b">
                  Received
                </th>
                <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase border-b">
                  Balance
                </th>
                <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase border-b">
                  Discount
                </th>
                <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase border-b">
                  Payment Amount
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr
                v-for="(invoice, index) in selectedInvoices"
                :key="index"
                class="hover:bg-gray-50"
              >
                <td class="px-4 py-2 text-sm text-gray-900 border-b">
                  {{ invoice.identifier }}
                </td>
                <td class="px-4 py-2 text-sm text-gray-600 border-b">
                  {{ formatDate(invoice.date) }}
                </td>
                <td class="px-4 py-2 text-sm text-right text-gray-900 border-b">
                  {{ invoice.amount | currency }}
                </td>
                <td class="px-4 py-2 text-sm text-right text-gray-600 border-b">
                  {{ invoice.received | currency }}
                </td>
                <td class="px-4 py-2 text-sm font-medium text-right border-b"
                  :class="invoice.balance > 0 ? 'text-red-600' : 'text-green-600'">
                  {{ invoice.balance | currency }}
                </td>
                <td class="px-4 py-2 text-sm border-b">
                  <input
                    v-model.number="discountAmounts[index]"
                    type="number"
                    min="0"
                    :max="invoice.balance"
                    class="w-full px-2 py-1 text-right border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    @input="updateDiscount(index, $event.target.value, invoice.balance)"
                    placeholder="0.00"
                  />
                </td>
                <td class="px-4 py-2 text-sm border-b">
                  <input
                    :value="getCalculatedPaymentAmount(index)"
                    type="number"
                    min="0"
                    class="w-full px-2 py-1 text-right border border-gray-300 rounded bg-gray-100"
                    readonly
                    style="cursor: not-allowed;"
                  />
                  <div class="text-xs text-gray-500 mt-1 text-right">
                    (Balance - Discount)
                  </div>
                </td>
              </tr>
            </tbody>
            <tfoot class="bg-gray-50">
              <tr>
                <td colspan="5" class="px-4 py-2 text-sm font-bold text-right">
                  Total Discount:
                </td>
                <td class="px-4 py-2 text-sm font-bold text-right text-orange-600">
                  {{ totalDiscount | currency }}
                </td>
                <td></td>
              </tr>
              <tr>
                <td colspan="5" class="px-4 py-2 text-sm font-bold text-right">
                  Total Payment:
                </td>
                <td class="px-4 py-2 text-sm font-bold text-right text-green-600">
                  {{ totalInvoicePayment | currency }}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <!-- Payment Modes -->
      <div class="mt-4 payment-modes">
        <div class="flex justify-between items-center mb-2">
          <h4 class="font-bold">Payment Details</h4>
          <button
            v-if="balance !== 0 && totalInvoicePayment > 0"
            @click="autoFillPaymentModes"
            class="px-3 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700"
          >
            Auto Fill Payment Modes
          </button>
        </div>
        <div class="flex flex-row justify-between gap-2">
          <div
            v-for="paymentMode in sortedPaymentModes"
            :key="paymentMode.paymentModeId"
            class="flex flex-col p-2 justify-between min-w-[300px] bg-white rounded-lg border"
          >
            <div class="w-1/3 font-medium">
              <a @click="handlePaymentModeClick(paymentMode)">{{
                paymentMode.label
              }}</a>
            </div>
            <div class="w-full">
              <input
                :value="paymentAmounts[paymentMode.paymentModeId] || 0"
                @input="
                  updatePaymentAmount(
                    paymentMode.paymentModeId,
                    $event.target.value
                  )
                "
                type="number"
                min="0"
                class="w-full border border-gray-300 form-input"
                :placeholder="`${paymentMode.label} amount`"
              />
            </div>
          </div>
        </div>

        <!-- Total Paid and Balance -->
        <div class="p-4 mt-2 bg-gray-100 rounded-lg payment-summary">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <strong>Paying: </strong>
              <span class="text-green-600">{{ totalPaid | currency }}</span>
            </div>
            <div>
              <strong>Balance: </strong>
              <span :class="balanceClass">{{ balance | currency }}</span>
            </div>
          </div>
          <div
            v-if="balance > 0"
            class="p-2 mt-2 text-yellow-800 bg-yellow-100 border border-yellow-400 rounded"
          >
            <i class="mr-1 fas fa-exclamation-triangle"></i>
            <strong>Warning:</strong> Payment breakdown ({{ totalPaid | currency }}) does not match total invoice payment ({{ totalInvoicePayment | currency }}). 
            Please adjust payment modes to match the invoice payment amount or use the "Auto Fill Payment Modes" button above.
          </div>
          <div
            v-if="balance < 0"
            class="p-2 mt-2 text-red-800 bg-red-100 border border-red-400 rounded"
          >
            <i class="mr-1 fas fa-exclamation-triangle"></i>
            <strong>Error:</strong> Payment amount ({{ totalPaid | currency }}) exceeds the total invoice payment ({{ totalInvoicePayment | currency }}) by
            {{ Math.abs(balance) | currency }}. Please reduce payment mode amounts.
          </div>
          <div
            v-if="balance === 0 && totalInvoicePayment > 0"
            class="p-2 mt-2 text-green-800 bg-green-100 border border-green-400 rounded"
          >
            <i class="mr-1 fas fa-check-circle"></i>
            <strong>Perfect!</strong> Payment breakdown matches the invoice payment amount.
          </div>
        </div>

        <!-- Payment Note -->
        <div class="mt-4 payment-note">
          <label class="block mb-1 font-medium">Payment Note</label>
          <textarea
            v-model="paymentNote"
            class="w-full border border-gray-300 rounded form-textarea"
            rows="3"
            placeholder="Enter any additional payment notes..."
          ></textarea>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end space-x-2">
        <button
          @click="handleClose"
          class="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          type="button"
          :disabled="saving || balance !== 0"
          @click="handleSave"
          class="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
        >
          <span v-if="saving" class="mr-2 fas fa-spinner fa-spin"></span>
          Save Payment
        </button>
      </div>
    </template>
  </modal>
</template>

<script>
import { mapGetters } from "vuex";
import Modal from "@/components/Modal.vue";

export default {
  name: "InvoicePaymentPopup",
  components: { Modal },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    selectedInvoices: {
      type: Array,
      default: () => []
    },
    customerName: {
      type: String,
      default: ""
    },
    firmName: {
      type: String,
      default: ""
    },
    saving: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      paymentAmounts: {},
      invoicePaymentAmounts: [],
      discountAmounts: [],
      paymentNote: "",
      internalShow: this.show
    };
  },
  computed: {
    ...mapGetters({
      paymentModeList: "paymentMode/getPaymentModes"
    }),
    totalDiscount() {
      return this.discountAmounts.reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );
    },
    totalInvoicePayment() {
      // Payment amount = (balance - discount) for each invoice
      return this.selectedInvoices.reduce((sum, invoice, index) => {
        const balance = parseFloat(invoice.balance) || 0;
        const discount = parseFloat(this.discountAmounts[index]) || 0;
        const paymentAmount = Math.max(0, balance - discount);
        return sum + paymentAmount;
      }, 0);
    },
    totalPaid() {
      return Object.values(this.paymentAmounts).reduce(
        (sum, amount) => sum + (parseFloat(amount) || 0),
        0
      );
    },
    balance() {
      return this.totalInvoicePayment - this.totalPaid;
    },
    balanceClass() {
      if (this.balance === 0) {
        return "text-green-600 font-bold";
      } else if (this.balance > 0) {
        return "text-orange-600 font-bold";
      } else {
        return "text-red-600 font-bold";
      }
    },
    sortedPaymentModes() {
      const order = ["UPI", "Cash", "Card", "Cheque", "Online"];
      return [...this.paymentModeList].sort((a, b) => {
        const aIndex = order.indexOf(a.label);
        const bIndex = order.indexOf(b.label);

        if (aIndex !== -1 && bIndex !== -1) {
          return aIndex - bIndex;
        }

        if (aIndex !== -1) return -1;
        if (bIndex !== -1) return 1;

        return a.label.localeCompare(b.label);
      });
    }
  },
  watch: {
    show(newVal) {
      this.internalShow = newVal;
      if (newVal) {
        this.initializePaymentAmounts();
        // Initialize invoice payment amounts and discounts when popup opens
        if (this.selectedInvoices && this.selectedInvoices.length > 0) {
          this.discountAmounts = this.selectedInvoices.map(() => 0);
          this.recalculatePaymentAmounts();
        }
      }
    },
    internalShow(newVal) {
      if (!newVal) {
        this.$emit("update:show", false);
      }
    }
  },
  methods: {
    initializePaymentAmounts() {
      this.paymentAmounts = {};
      this.paymentNote = "";
      // Initialize all payment modes with 0
      this.paymentModeList.forEach(mode => {
        this.$set(this.paymentAmounts, mode.paymentModeId, 0);
      });
    },
    updatePaymentAmount(modeId, value) {
      this.$set(this.paymentAmounts, modeId, parseFloat(value) || 0);
    },
    updateDiscount(index, value, balance) {
      const discount = parseFloat(value) || 0;
      const maxDiscount = parseFloat(balance) || 0;
      
      // Ensure discount doesn't exceed balance
      if (discount > maxDiscount) {
        this.$set(this.discountAmounts, index, maxDiscount);
        this.$notify({
          message: `Discount cannot exceed balance of ${this.$options.filters.currency(maxDiscount)}`,
          type: "warning",
          title: "Warning"
        });
      } else {
        this.$set(this.discountAmounts, index, discount);
      }
      
      // Recalculate payment amount for this invoice
      this.recalculatePaymentAmount(index);
    },
    recalculatePaymentAmount(index) {
      const invoice = this.selectedInvoices[index];
      const balance = parseFloat(invoice.balance) || 0;
      const discount = parseFloat(this.discountAmounts[index]) || 0;
      const paymentAmount = Math.max(0, balance - discount);
      this.$set(this.invoicePaymentAmounts, index, paymentAmount);
    },
    recalculatePaymentAmounts() {
      // Recalculate payment amounts for all invoices
      this.selectedInvoices.forEach((invoice, index) => {
        this.recalculatePaymentAmount(index);
      });
    },
    getMaxPaymentAmount(index) {
      const invoice = this.selectedInvoices[index];
      const balance = parseFloat(invoice.balance) || 0;
      const discount = parseFloat(this.discountAmounts[index]) || 0;
      return Math.max(0, balance - discount);
    },
    getCalculatedPaymentAmount(index) {
      const invoice = this.selectedInvoices[index];
      const balance = parseFloat(invoice.balance) || 0;
      const discount = parseFloat(this.discountAmounts[index]) || 0;
      return Math.max(0, balance - discount);
    },
    updateInvoicePayment(index, value) {
      // Payment amount is now read-only and calculated from balance - discount
      // This method is kept for compatibility but shouldn't be called
      // The input is readonly, so this shouldn't trigger
    },
    handleClose() {
      this.internalShow = false;
    },
    handleSave() {
      // Validate that payment breakdown matches invoice payment
      if (this.balance !== 0) {
        if (this.balance < 0) {
          this.$notify({
            message: "Payment amount cannot exceed total invoice payment",
            type: "danger",
            title: "Error"
          });
        } else {
          this.$notify({
            message: `Payment breakdown (${this.$options.filters.currency(this.totalPaid)}) must match total invoice payment (${this.$options.filters.currency(this.totalInvoicePayment)}). Please adjust payment modes.`,
            type: "danger",
            title: "Validation Error"
          });
        }
        return;
      }
      
      // Ensure at least one payment mode has amount > 0
      if (this.totalPaid === 0) {
        this.$notify({
          message: "Please enter payment amount in at least one payment mode",
          type: "warning",
          title: "Validation Error"
        });
        return;
      }
      
      this.$emit("save", this.getPaymentData());
    },
    handlePaymentModeClick(paymentMode) {
      // Set payment mode amount to match total invoice payment
      // First, clear all other payment modes
      Object.keys(this.paymentAmounts).forEach(modeId => {
        this.$set(this.paymentAmounts, modeId, 0);
      });
      // Then set the clicked payment mode to total invoice payment
      this.$set(this.paymentAmounts, paymentMode.paymentModeId, this.totalInvoicePayment);
    },
    autoFillPaymentModes() {
      // Clear all payment modes first
      Object.keys(this.paymentAmounts).forEach(modeId => {
        this.$set(this.paymentAmounts, modeId, 0);
      });
      
      // If there's only one payment mode, fill it completely
      if (this.sortedPaymentModes.length === 1) {
        this.$set(this.paymentAmounts, this.sortedPaymentModes[0].paymentModeId, this.totalInvoicePayment);
      } else {
        // Fill the first payment mode with the total invoice payment
        if (this.sortedPaymentModes.length > 0) {
          this.$set(this.paymentAmounts, this.sortedPaymentModes[0].paymentModeId, this.totalInvoicePayment);
        }
      }
    },
    getPaymentData() {
      const paymentBreakdown = Object.keys(this.paymentAmounts)
        .filter(modeId => this.paymentAmounts[modeId] > 0)
        .map(modeId => {
          const mode = this.paymentModeList.find(
            m => m.paymentModeId === modeId
          );
          return {
            paymentModeId: modeId,
            paymentModeLabel: mode ? mode.label : "",
            amount: parseFloat(this.paymentAmounts[modeId])
          };
        });
        
      // Map invoice payments
      const invoicePayments = this.selectedInvoices.map((invoice, index) => {
        const balance = parseFloat(invoice.balance) || 0;
        const discount = parseFloat(this.discountAmounts[index]) || 0;
        const paymentAmount = Math.max(0, balance - discount);
        
        return {
          invoiceId: invoice.identifier,
          invoiceAmount: parseFloat(invoice.amount) || 0,
          id: invoice.id,
          paymentAmount: paymentAmount,
          discount: discount,
          balance: balance,
          firm: invoice.firm || '',
          serviceRequestNumber: invoice.serviceRequestNumber || null
        };
      });

      return {
        totalPaid: this.totalPaid,
        totalInvoicePayment: this.totalInvoicePayment,
        balance: this.balance,
        paymentNote: this.paymentNote,
        paymentBreakdown,
        invoicePayments,
        selectedInvoices: this.selectedInvoices
      };
    },
    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      return d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      });
    }
  },
  filters: {
    currency(value) {
      const amount = parseFloat(value) || 0;
      return new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR"
      }).format(amount);
    }
  }
};
</script>

<style scoped>
.invoice-payment-popup {
  max-height: 70vh;
  overflow-y: auto;
}
</style>

<style>
/* Custom modal width for invoice payment popup */
.modal-xl {
  max-width: 1200px;
}
</style>

